import { Typography } from "@mui/material";
import { Link } from 'react-router-dom';
import { ActionsCell } from "../../../Component/Common/Components/DataTable/ActionsCell";
import { ClickableCell } from "../../../Component/Common/Components/DataTable/ClickableCell";
import { DefaultCell } from "../../../Component/Common/Components/DataTable/DefaultCell";
import { IClickableCell } from "../../../Component/Common/Components/DataTable/RowAction";
import { ScorecardAdmin, SubAdminRole, UserAdminRole } from "../../../Component/Common/Constants/UserRoles";
import { ScorecardVA } from "../../../Component/Model/Dto/ScorecardLicense";
import { ActionType, ILinkProps, TableColumn } from "../../../Component/Model/Table/Table";
import { DateFormatter } from "../../../Component/Model/helpers";
import { decimalFormatter } from "../../../Helpers/DecimalFormatter";
import config from "../../../config";

interface openScorecardTableRow {
    id: string;
    name: ILinkProps;
    scorecardType: string;
    createdDate: string;
    isConsolidated: string;
    fromDate: string;
    toDate: string;
    totalPoints: number | string;
    adjustedLevel: string;
    progressTracker: IClickableCell;
    actions: ActionType[];
}

export interface IOpenScorecardsTable {
    columns: Array<TableColumn>;
    rows: Array<openScorecardTableRow>;
}

export function VALockedScorecardTableMapper(results: ScorecardVA.ScorecardDataCore[], handleAssignUser: (id: string) => void, unlockScorecard: (id: string) => void, deleteScorecard: (id: string) => void, openViewCertificates: (id: string) => void, handleStatusTracker: (id: string) => void, userRoles: string[]): IOpenScorecardsTable {
    const columns = [
        { Header: 'Name', accessor: 'name', Cell: ({ value }: any) => <Typography variant="caption" fontWeight='regular' color="#007bff" component={Link} to={value.linkTo}>{value.itemName}</Typography> },
        { Header: 'Scorecard Type', accessor: 'scorecardType', id: "scorecardTypeName", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Created Date', accessor: 'createdDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Consolidated Scorecard', accessor: 'isConsolidated', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'From Date', accessor: 'fromDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'To Date', accessor: 'toDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Verified Total Points', accessor: 'totalPoints', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Verified Rating Level', accessor: 'ratingLevel', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Verified Adjusted Level', accessor: 'adjustedLevel', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Planner and Tracker', accessor: 'progressTracker', Cell: ({ value }: any) => <ClickableCell value={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true }
    ];
    
    const handleLinkClick = (item: ScorecardVA.ScorecardDataCore) => {
        if(userRoles.includes(ScorecardAdmin)){
            window.location.href = `${config.publicUrl}#general/${item.id}`
        }
        else{
            window.location.href = `${config.publicUrl}#scorecard/${item.id}/dashboard`
        }
    };

    function getActionsColumn(item: ScorecardVA.ScorecardDataCore, index: number) {
        const actions: ActionType[] = [
            item.hasCertificates ?
                {
                    key: index.toString(),
                    label: 'View Certificates',
                    action: () => {
                        openViewCertificates(item.id);
                    },
                    icon: {
                        color: 'primary',
                        name: 'grading'
                    }
                }
                :
                {
                    key: index.toString(),
                    label: `There are no certificates available to download`,
                    action: () => { },
                    icon: {
                        color: 'secondary',
                        name: 'grading'
                    },
                    disabled: true
                }
        ];

        if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole)) {
            if (item.isReevaluation || item.isSharedForVerification) {
                actions.push({
                    key: `unlock_${index}`,
                    label: `Scorecard submitted for ${(item.isReevaluation ? 'Re-evaluation' : 'verification')}`,
                    action: () => { },
                    icon: {
                        color: 'error',
                        name: 'lock_open'
                    },
                    disabled: true
                });
            } else {
                actions.push({
                    key: `unlock_${index}`,
                    label: 'Unlock Scorecard',
                    action: () => {
                        unlockScorecard(item.id);
                    },
                    icon: {
                        color: 'primary',
                        name: 'lock_open'
                    }
                });
            }
        }

        if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole) || userRoles.includes(ScorecardAdmin)) {
            actions.push({
                key: `assign_${index}`,
                label: 'Assign Users',
                action: () => {
                    handleAssignUser(item.id);
                },
                icon: {
                    color: 'primary',
                    name: 'person_add'
                }
            });
        }

        if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole) || userRoles.includes(ScorecardAdmin)) {
            actions.push({
                key: `delete_${index}`,
                label: 'Delete',
                action: () => {
                    deleteScorecard(item.id);
                },
                icon: {
                    color: 'error',
                    name: 'delete'
                }
            });
        }

        return actions;
    }

    if (results.length >= 0) {
        const rows = results.map((item: ScorecardVA.ScorecardDataCore, index: number) => (
            {
                id: item.id,
                name: {
                    linkTo: `/scorecardManagement/LockedScorecard`,
                    itemName: (
                        <div
                            style={{ cursor: "pointer" }}
                            onClick={() => handleLinkClick(item)}
                        >
                            {item.name}
                        </div>
                    ),
                },
                scorecardType: item.scorecardTypeName,
                createdDate: item.createdDate ? DateFormatter(item.createdDate.toString()) : '',
                isConsolidated: item.isConsolidated ? 'Yes' : 'No',
                fromDate: item.fromDate ? DateFormatter(item.fromDate.toString()) : '',
                toDate: item.toDate ? DateFormatter(item.toDate.toString()) : '',
                totalPoints: decimalFormatter(item.totalPoints, 2),
                ratingLevel: item.ratingLevel,
                adjustedLevel: item.adjustedLevel,
                progressTracker: {
                    label: item.verificationStatus,
                    key: item.id,
                    action: () => {
                        handleStatusTracker(item.id)
                    }
                },
                actions: getActionsColumn(item, index)
            }));
        return { columns: columns, rows: rows };
    } else {
        return { columns: columns, rows: [] };
    }
}

